import type {
  ApiKeyCreatedDto,
  ApiKeyDto,
  ContentDetailDto,
  ContentRevisionDetailDto,
  ContentRevisionSummaryDto,
  ContentSummaryDto,
  ImageDto,
  ProjectDto,
  ProjectMemberDto,
  ProjectMemberRole,
  UserRowDto,
} from "@shared/types";
import type { UiMode } from "@shared/schemas";
import { apiErrorMessage } from "./api-error-message";
import {
  redirectIfUnauthorized,
  resolveAdminApiUrl,
} from "./platform-admin";

// Mirrors EmailFailureReason in apps/api/src/lib/email.ts. Returned alongside
// emailSent: false so the invite screens can tell "retry may work" apart from
// "the sending quota is gone, retrying just fails again".
export type EmailFailureCode = "quota_exhausted" | "unknown";

class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const resolvedInput =
    typeof input === "string" ? resolveAdminApiUrl(input) : input;
  const res = await fetch(resolvedInput, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    redirectIfUnauthorized();
    throw new ApiError(401, "unauthorized", null);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    throw new ApiError(res.status, apiErrorMessage(res.status, payload), payload);
  }
  return payload as T;
}

function segment(value: string): string {
  return encodeURIComponent(value);
}

export const api = {
  async getPlatformSession(): Promise<{
    identity: { subject: string; email: string | null };
  }> {
    return request("/platform-admin/api/session");
  },
  async listProjects(): Promise<{ items: ProjectDto[] }> {
    return request("/api/admin/projects");
  },
  async getProject(id: string): Promise<ProjectDto> {
    return request(`/api/admin/projects/${segment(id)}`);
  },
  async updateProject(
    id: string,
    patch: {
      name?: string;
      deployHookUrl?: string | null;
      frontmatterTemplate?: string | null;
    },
  ): Promise<ProjectDto> {
    return request(`/api/admin/projects/${segment(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
  },
  async triggerDeploy(
    id: string,
  ): Promise<{ ok: boolean; status: number; body: string }> {
    return request(`/api/admin/projects/${segment(id)}/deploy`, { method: "POST" });
  },
  async deleteProject(id: string, confirmSlug: string): Promise<void> {
    return request(`/api/admin/projects/${segment(id)}`, {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ confirmSlug }),
    });
  },
  async listKeys(projectId: string): Promise<{ items: ApiKeyDto[] }> {
    return request(`/api/admin/projects/${segment(projectId)}/keys`);
  },
  async createKey(
    projectId: string,
    input: { name: string; expiresAt?: string },
  ): Promise<ApiKeyCreatedDto> {
    return request(`/api/admin/projects/${segment(projectId)}/keys`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  },
  async revokeKey(projectId: string, keyId: string): Promise<ApiKeyDto> {
    return request(`/api/admin/projects/${segment(projectId)}/keys/${segment(keyId)}`, {
      method: "DELETE",
    });
  },
  async listContents(projectIdOrSlug: string): Promise<{ items: ContentSummaryDto[] }> {
    return request(`/api/admin/contents/${segment(projectIdOrSlug)}`);
  },
  async getContent(projectIdOrSlug: string, slug: string): Promise<ContentDetailDto> {
    return request(`/api/admin/contents/${segment(projectIdOrSlug)}/${segment(slug)}`);
  },
  async putContent(
    projectIdOrSlug: string,
    slug: string,
    body: string,
  ): Promise<ContentDetailDto> {
    return request(`/api/admin/contents/${segment(projectIdOrSlug)}/${segment(slug)}`, {
      method: "PUT",
      headers: { "content-type": "text/markdown" },
      body,
    });
  },
  async deleteContent(projectIdOrSlug: string, slug: string): Promise<void> {
    return request(`/api/admin/contents/${segment(projectIdOrSlug)}/${segment(slug)}`, {
      method: "DELETE",
    });
  },
  async listRevisions(
    projectIdOrSlug: string,
    slug: string,
  ): Promise<{ items: ContentRevisionSummaryDto[] }> {
    return request(`/api/admin/contents/${segment(projectIdOrSlug)}/${segment(slug)}/revisions`);
  },
  async getRevision(
    projectIdOrSlug: string,
    slug: string,
    revisionId: string,
  ): Promise<ContentRevisionDetailDto> {
    return request(
      `/api/admin/contents/${segment(projectIdOrSlug)}/${segment(slug)}/revisions/${segment(revisionId)}`,
    );
  },
  async restoreRevision(
    projectIdOrSlug: string,
    slug: string,
    revisionId: string,
  ): Promise<{
    content: ContentDetailDto;
    revisions: ContentRevisionSummaryDto[];
  }> {
    return request(
      `/api/admin/contents/${segment(projectIdOrSlug)}/${segment(slug)}/revisions/${segment(revisionId)}/restore`,
      { method: "POST" },
    );
  },
  async listImages(projectIdOrSlug: string): Promise<{ items: ImageDto[] }> {
    return request(`/api/admin/images/${segment(projectIdOrSlug)}`);
  },
  async uploadImage(input: {
    projectIdOrSlug: string;
    file: File;
    width?: number | null;
    height?: number | null;
  }): Promise<ImageDto> {
    const fd = new FormData();
    fd.append("file", input.file);
    if (input.width != null) fd.append("width", String(input.width));
    if (input.height != null) fd.append("height", String(input.height));
    return request(`/api/admin/images/${segment(input.projectIdOrSlug)}`, {
      method: "POST",
      body: fd,
    });
  },
  async deleteImage(projectIdOrSlug: string, r2Key: string): Promise<void> {
    return request(`/api/admin/images/${segment(projectIdOrSlug)}/${r2Key}`, {
      method: "DELETE",
    });
  },
  // Platform-console only. The server mounts adminUsers under
  // /platform-admin/api/users and nowhere else, so these spell the real path
  // out (like getPlatformSession above) instead of writing /api/admin/users and
  // relying on resolveAdminApiUrl happening to rewrite it — that only works
  // while the caller is on a /platform-admin page, which made these three
  // silently 404 from anywhere else.
  async listUsers(): Promise<{ users: UserRowDto[] }> {
    return request("/platform-admin/api/users");
  },
  async inviteUser(input: {
    email: string;
    name?: string;
    projectSlug: string;
    projectName: string;
  }): Promise<{
    ok: true;
    userId: string;
    projectId: string;
    isNewUser: boolean;
    emailSent: boolean;
    emailError?: string;
    emailErrorCode?: EmailFailureCode;
  }> {
    return request("/platform-admin/api/users", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  },
  async deleteUser(userId: string): Promise<void> {
    return request(`/platform-admin/api/users/${segment(userId)}`, {
      method: "DELETE",
    });
  },
  async listMembers(
    projectIdOrSlug: string,
  ): Promise<{ items: ProjectMemberDto[] }> {
    return request(`/api/admin/projects/${segment(projectIdOrSlug)}/members`);
  },
  async inviteMember(
    projectIdOrSlug: string,
    input: { email: string; name?: string; role?: ProjectMemberRole },
  ): Promise<{
    userId: string;
    role: ProjectMemberRole;
    // Only returned to a verified platform admin (cross-tenant signal).
    isNewUser?: boolean;
    alreadyMember: boolean;
    emailSent: boolean;
    emailError?: string;
    emailErrorCode?: EmailFailureCode;
  }> {
    return request(`/api/admin/projects/${segment(projectIdOrSlug)}/members`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  },
  async updateMemberRole(
    projectIdOrSlug: string,
    userId: string,
    role: ProjectMemberRole,
  ): Promise<{ userId: string; role: ProjectMemberRole }> {
    return request(`/api/admin/projects/${segment(projectIdOrSlug)}/members/${segment(userId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role }),
    });
  },
  async removeMember(
    projectIdOrSlug: string,
    userId: string,
  ): Promise<void> {
    return request(`/api/admin/projects/${segment(projectIdOrSlug)}/members/${segment(userId)}`, {
      method: "DELETE",
    });
  },
  async updateUiMode(uiMode: UiMode): Promise<{ uiMode: UiMode }> {
    return request("/api/admin/me/ui-mode", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ uiMode }),
    });
  },
};

export { ApiError };
